import { SECONDS, freezerApi, thunks } from "@app/api";
import { fetchApp } from "@app/deploy/app";
import { fetchDatabase } from "@app/deploy/database";
import { fetchEndpoint } from "@app/deploy/endpoint";
import { fetchEnvironmentById } from "@app/deploy/environment";
import { fetchLogDrain } from "@app/deploy/log-drain";
import { fetchMetricDrain } from "@app/deploy/metric-drain";
import { fetchOperationById } from "@app/deploy/operation";
import { fetchService } from "@app/deploy/service";
import { call, createAction, createSelector, poll, select } from "@app/fx";
import type { Operation } from "@app/fx";
import {
  appDetailUrl,
  appServiceUrl,
  billingInvoicesUrl,
  certDetailUrl,
  databaseBackupsUrl,
  databaseCredentialsUrl,
  databaseDetailUrl,
  endpointDetailActivityUrl,
  environmentDetailUrl,
  environmentIntegrationsUrl,
  externalAwsResourceConnectionsUrl,
  llmKeyDetailUrl,
  operationDetailUrl,
  plansUrl,
  roleDetailUrl,
  settingsAccountIntegrationsAwsResourcesUrl,
  sourceDetailAppsUrl,
  stackDetailEnvsUrl,
  teamContactsUrl,
  teamMembersUrl,
} from "@app/routes";
import { type WebState, schema } from "@app/schema";
import { titleCase } from "@app/string-utils";
import type { SelectOption } from "@app/ui/shared/select";

export interface FreezerActivityFilters {
  organizationId: string;
  occurredAfter?: string;
  occurredBefore?: string;
  page?: number;
  perPage?: number;
  types?: string[];
  statuses?: string[];
  actors?: string[];
  envs?: string[];
  targetTypes?: string[];
  resourceType?: string;
  resourceId?: string;
  q?: string;
  readOnly?: boolean;
}

export interface FreezerFilterOption {
  value: string;
  title: string;
  readOnly?: boolean;
}

export interface FreezerFilterOptionResponse {
  value: string;
  title: string;
  read_only?: boolean;
}

const deserializeFreezerFilterOption = (
  data: FreezerFilterOptionResponse,
): FreezerFilterOption => ({
  value: data.value,
  title: data.title,
  readOnly: data.read_only,
});

export interface FreezerPaginationMeta {
  page: number;
  totalPages: number;
  totalCount: number;
  perPage: number;
}

export const FREEZER_PAGINATION_KEY = "freezer-activities-pagination";
export const FREEZER_FILTER_TYPES_KEY = "freezer-filter-types";
export const FREEZER_FILTER_STATUSES_KEY = "freezer-filter-statuses";
export const FREEZER_FILTER_TARGET_TYPES_KEY = "freezer-filter-target-types";
export const FREEZER_FILTER_ACTORS_KEY = "freezer-filter-actors";
export const FREEZER_FILTER_ACCOUNTS_CACHE = "freezer-filter-accounts";

export const defaultFreezerPagination = (): FreezerPaginationMeta => ({
  page: 1,
  totalPages: 1,
  totalCount: 0,
  perPage: 40,
});

export const selectFreezerPagination = createSelector(
  (state: Parameters<typeof schema.cache.selectById>[0]) =>
    schema.cache.selectById(state, { id: FREEZER_PAGINATION_KEY }),
  (cached) => (cached as FreezerPaginationMeta) || defaultFreezerPagination(),
);

export interface FreezerTargetResource {
  type: string;
  id: string;
  identifier: string;
}

export interface FreezerActivity {
  id: string;
  activityType: string;
  title: string;
  occurredAt: string;
  primaryResourceType: string;
  primaryResourceId: string;
  status: string;
  targetResources: FreezerTargetResource[];
  organizationId: string;
  client?: string;
  description?: Record<string, unknown>;
  ipAddress?: string;
  userEmail?: string;
  userName?: string;
  accountId?: string;
  externalId?: string;
  tokenId?: string;
  userId?: string;
  sourceSystem?: string;
  summary: string;
  createdAt: string;
  updatedAt: string;
}

export interface FreezerActivityAccount {
  value: string;
  title: string;
  deleted?: boolean;
}

export interface FreezerActivityResponse {
  id: number;
  activity_type: string;
  title: string;
  occurred_at: string;
  primary_resource_type: string;
  primary_resource_id: number;
  status: string;
  target_resources: FreezerTargetResource[];
  organization_id: string;
  client?: string | null;
  description?: Record<string, unknown> | null;
  ip_address?: string | null;
  user_email?: string | null;
  user_name?: string | null;
  account_id?: number | null;
  external_id?: string | null;
  token_id?: string | null;
  user_id?: string | null;
  summary: string;
  source_system?: string | null;
  created_at: string;
  updated_at: string;
}

export interface FreezerActivityAccountsResponseInstance {
  value: number;
  title: string;
}

export interface FreezerActivityAccountsResponse {
  results: FreezerActivityAccountsResponseInstance[];
}

export const defaultFreezerActivity = (
  a: Partial<FreezerActivity> = {},
): FreezerActivity => {
  const now = new Date().toISOString();
  return {
    id: "",
    activityType: "",
    title: "",
    occurredAt: now,
    primaryResourceType: "",
    primaryResourceId: "",
    status: "succeeded",
    targetResources: [],
    organizationId: "",
    client: "",
    description: {},
    ipAddress: "",
    userEmail: "",
    userName: "",
    accountId: "",
    externalId: "",
    tokenId: "",
    userId: "",
    sourceSystem: "",
    summary: "",
    createdAt: now,
    updatedAt: now,
    ...a,
  };
};

export const deserializeFreezerActivity = (
  data: FreezerActivityResponse,
): FreezerActivity => {
  return {
    id: `${data.id}`,
    activityType: data.activity_type,
    title: data.title || "",
    occurredAt: data.occurred_at,
    primaryResourceType: data.primary_resource_type,
    primaryResourceId: `${data.primary_resource_id}`,
    status: data.status,
    targetResources: data.target_resources || [],
    organizationId: data.organization_id,
    client: data.client || "",
    description: data.description || {},
    ipAddress: data.ip_address || "",
    userEmail: data.user_email || "",
    userName: data.user_name || "",
    accountId: data.account_id ? `${data.account_id}` : "",
    externalId: data.external_id || "",
    tokenId: data.token_id || "",
    userId: data.user_id || "",
    sourceSystem: data.source_system || "",
    summary: data.summary || "",
    createdAt: data.created_at,
    updatedAt: data.updated_at,
  };
};

export const deserializeFreezerActivityAccounts = (
  data: FreezerActivityAccountsResponseInstance,
): FreezerActivityAccount => {
  return {
    value: data.value ? `${data.value}` : "",
    title: data.title,
  };
};

export const FREEZER_ACTIVITIES_KEY = "freezer-activities";

export const clearFreezerActivities = thunks.create(
  "clear-freezer-activities",
  function* (_, next) {
    yield* schema.update(
      schema.cache.remove([FREEZER_ACTIVITIES_KEY, FREEZER_PAGINATION_KEY]),
    );
    yield* next();
  },
);

export const selectFreezerActivitiesAsList = createSelector(
  (state: Parameters<typeof schema.cache.selectById>[0]) =>
    schema.cache.selectById(state, { id: FREEZER_ACTIVITIES_KEY }),
  (cached) => (cached as FreezerActivity[]) || [],
);

const POLL_FREQUENCY = 10 * SECONDS;

export const cancelActivitiesPoll = createAction(
  "cancel-freezer-activities-poll",
);

function buildActivitiesQueryString(filters: FreezerActivityFilters): string {
  const params = new URLSearchParams();
  if (filters.organizationId) {
    params.set("organization_id", filters.organizationId);
  }
  if (filters.occurredAfter) {
    params.set("occurred_after", filters.occurredAfter);
  }
  if (filters.occurredBefore) {
    params.set("occurred_before", filters.occurredBefore);
  }
  if (filters.page) {
    params.set("page", filters.page.toString());
  }
  if (filters.perPage) {
    params.set("per_page", filters.perPage.toString());
  }
  if (filters.types?.length) {
    params.set("types", filters.types.join(","));
  }
  if (filters.statuses?.length) {
    params.set("statuses", filters.statuses.join(","));
  }
  if (filters.actors?.length) {
    params.set("actors", filters.actors.join(","));
  }
  if (filters.envs?.length) {
    params.set("envs", filters.envs.join(","));
  }
  if (filters.targetTypes?.length) {
    params.set("target_types", filters.targetTypes.join(","));
  }
  if (filters.resourceType) {
    params.set("resource_type", filters.resourceType);
  }
  if (filters.resourceId) {
    params.set("resource_id", filters.resourceId);
  }
  if (filters.q) {
    params.set("q", filters.q);
  }
  if (filters.readOnly) {
    params.set("read_only", "true");
  }
  const queryString = params.toString();
  return queryString ? `?${queryString}` : "";
}

function extractPaginationFromHeaders(
  response: Response | null | undefined,
  currentPage: number,
): FreezerPaginationMeta {
  if (!response) {
    return defaultFreezerPagination();
  }

  const totalCount = Number.parseInt(
    response.headers.get("X-Total-Count") || "0",
    10,
  );
  const totalPages = Number.parseInt(
    response.headers.get("X-Total-Pages") || "1",
    10,
  );

  return {
    page: currentPage,
    totalPages,
    totalCount,
    perPage: 40,
  };
}

export const pollActivities = freezerApi.get<FreezerActivityFilters>(
  ["/activities", "poll"],
  { supervisor: poll(POLL_FREQUENCY, `${cancelActivitiesPoll}`) },
  function* (ctx, next) {
    const filters = ctx.payload || {};
    const queryString = buildActivitiesQueryString(filters);
    const baseUrl = ctx.req().url;
    ctx.request = ctx.req({ url: `${baseUrl}${queryString}` });

    yield* next();

    if (!ctx.json.ok) {
      return;
    }

    const { activities: rawActivities } = ctx.json.value as {
      activities: FreezerActivityResponse[];
    };
    const activities = rawActivities.map(deserializeFreezerActivity);

    const pagination = extractPaginationFromHeaders(
      ctx.response,
      filters.page || 1,
    );

    yield* schema.update(
      schema.cache.add({
        [FREEZER_ACTIVITIES_KEY]: activities,
        [FREEZER_PAGINATION_KEY]: pagination,
      }),
    );
  },
);

export const FREEZER_ACTIVITY_BY_ID_KEY = "freezer-activity-by-id";

export const selectFreezerActivityById = createSelector(
  (
    state: Parameters<typeof schema.cache.selectById>[0],
    props: { id: string },
  ) =>
    schema.cache.selectById(state, {
      id: `${FREEZER_ACTIVITY_BY_ID_KEY}-${props.id}`,
    }),
  (cached) => (cached as FreezerActivity) || defaultFreezerActivity(),
);

export const fetchActivityById = freezerApi.get<{ id: string }>(
  "/activities/:id",
  function* (ctx, next) {
    yield* next();

    if (!ctx.json.ok) {
      return;
    }

    const rawActivity = ctx.json.value as FreezerActivityResponse;
    const activity = deserializeFreezerActivity(rawActivity);

    yield* schema.update(
      schema.cache.add({
        [`${FREEZER_ACTIVITY_BY_ID_KEY}-${ctx.payload.id}`]: activity,
      }),
    );
  },
);

export const FREEZER_ACTIVITY_BY_OPERATION_KEY =
  "freezer-activity-by-operation";

export const selectFreezerActivityByOperationId = createSelector(
  (
    state: Parameters<typeof schema.cache.selectById>[0],
    props: { operationId: string },
  ) =>
    schema.cache.selectById(state, {
      id: `${FREEZER_ACTIVITY_BY_OPERATION_KEY}-${props.operationId}`,
    }),
  (cached) => (cached as FreezerActivity) || defaultFreezerActivity(),
);

export const fetchActivityByOperationId = freezerApi.get<{
  operationId: string;
}>("/operations/:operationId", function* (ctx, next) {
  yield* next();

  if (!ctx.json.ok) {
    return;
  }

  const rawActivity = ctx.json.value as FreezerActivityResponse;
  const activity = deserializeFreezerActivity(rawActivity);

  yield* schema.update(
    schema.cache.add({
      [`${FREEZER_ACTIVITY_BY_OPERATION_KEY}-${ctx.payload.operationId}`]:
        activity,
      [`${FREEZER_ACTIVITY_BY_ID_KEY}-${activity.id}`]: activity,
    }),
  );
});

export const cancelActivityByIdPoll = createAction(
  "cancel-activity-by-id-poll",
);
export const pollActivityById = freezerApi.get<{ id: string }>(
  ["/activities/:id", "poll"],
  { supervisor: poll(5 * 1000, `${cancelActivityByIdPoll}`) },
  function* (ctx, next) {
    yield* next();

    if (!ctx.json.ok) {
      return;
    }

    const rawActivity = ctx.json.value as FreezerActivityResponse;
    const activity = deserializeFreezerActivity(rawActivity);

    const cacheKey = `${FREEZER_ACTIVITY_BY_ID_KEY}-${ctx.payload.id}`;
    const cached = yield* select(
      (s: WebState) =>
        schema.cache.selectById(s, { id: cacheKey }) as FreezerActivity | null,
    );

    if (
      cached?.status === activity.status &&
      Boolean(cached?.description?.cancelled) ===
        Boolean(activity.description?.cancelled)
    ) {
      return;
    }

    yield* schema.update(schema.cache.add({ [cacheKey]: activity }));
  },
);

// Filter discovery endpoints
export const fetchActivityTypes = freezerApi.get(
  "/activity_types?per_page=5000",
  function* (ctx, next) {
    yield* next();
    if (!ctx.json.ok) return;
    const { results: rawResults } = ctx.json.value as {
      results: FreezerFilterOptionResponse[];
    };
    const results = rawResults.map(deserializeFreezerFilterOption);
    yield* schema.update(
      schema.cache.add({ [FREEZER_FILTER_TYPES_KEY]: results }),
    );
  },
);

export const fetchActivityStatuses = freezerApi.get(
  "/activity_statuses?per_page=5000",
  function* (ctx, next) {
    yield* next();
    if (!ctx.json.ok) return;
    const { results } = ctx.json.value as {
      results: FreezerFilterOption[];
    };
    yield* schema.update(
      schema.cache.add({ [FREEZER_FILTER_STATUSES_KEY]: results }),
    );
  },
);

export const fetchActivityTargetTypes = freezerApi.get(
  "/activity_target_types?per_page=5000",
  function* (ctx, next) {
    yield* next();
    if (!ctx.json.ok) return;
    const { results } = ctx.json.value as {
      results: FreezerFilterOption[];
    };
    yield* schema.update(
      schema.cache.add({ [FREEZER_FILTER_TARGET_TYPES_KEY]: results }),
    );
  },
);

export const fetchActivityActors = freezerApi.get(
  "/activity_actors?per_page=5000",
  function* (ctx, next) {
    yield* next();
    if (!ctx.json.ok) return;
    const { results } = ctx.json.value as {
      results: FreezerFilterOption[];
    };
    yield* schema.update(
      schema.cache.add({ [FREEZER_FILTER_ACTORS_KEY]: results }),
    );
  },
);

export const fetchActivityAccounts = freezerApi.get(
  "/activity_accounts?per_page=5000",
  function* (ctx, next) {
    yield* next();
    if (!ctx.json.ok) return;

    const { results: rawFreezerActivityAccounts } = ctx.json
      .value as FreezerActivityAccountsResponse;
    const results = rawFreezerActivityAccounts.map(
      deserializeFreezerActivityAccounts,
    );

    yield* schema.update(
      schema.cache.add({ [FREEZER_FILTER_ACCOUNTS_CACHE]: results }),
    );
  },
);

// Filter selectors
const mapFilterOptionsToSelectOptions = (
  options: FreezerFilterOption[],
): SelectOption[] => {
  const seen = new Set<string>();
  return options
    .filter((o) => {
      if (seen.has(o.title)) {
        return false;
      }
      seen.add(o.title);
      return true;
    })
    .map((o) => ({
      value: o.value,
      label: o.title,
      readOnly: o.readOnly,
    }))
    .sort((a, b) => a.label.localeCompare(b.label));
};

export const selectFreezerFilterTypes = createSelector(
  (state: Parameters<typeof schema.cache.selectById>[0]) =>
    schema.cache.selectById(state, { id: FREEZER_FILTER_TYPES_KEY }),
  (cached) =>
    mapFilterOptionsToSelectOptions((cached as FreezerFilterOption[]) || []),
);

export const selectFreezerFilterStatuses = createSelector(
  (state: Parameters<typeof schema.cache.selectById>[0]) =>
    schema.cache.selectById(state, { id: FREEZER_FILTER_STATUSES_KEY }),
  (cached) =>
    mapFilterOptionsToSelectOptions((cached as FreezerFilterOption[]) || []),
);

export const selectFreezerFilterTargetTypes = createSelector(
  (state: Parameters<typeof schema.cache.selectById>[0]) =>
    schema.cache.selectById(state, { id: FREEZER_FILTER_TARGET_TYPES_KEY }),
  (cached) =>
    mapFilterOptionsToSelectOptions((cached as FreezerFilterOption[]) || []),
);

export const selectFreezerFilterActors = createSelector(
  (state: Parameters<typeof schema.cache.selectById>[0]) =>
    schema.cache.selectById(state, { id: FREEZER_FILTER_ACTORS_KEY }),
  (cached) =>
    mapFilterOptionsToSelectOptions((cached as FreezerFilterOption[]) || []),
);

export const selectFreezerFilterAccounts = createSelector(
  (state: Parameters<typeof schema.cache.selectById>[0]) =>
    schema.cache.selectById(state, { id: FREEZER_FILTER_ACCOUNTS_CACHE }),
  (cached) =>
    mapFilterOptionsToSelectOptions((cached as FreezerFilterOption[]) || []),
);

export const selectFreezerFilterAccountsWithStatus = createSelector(
  (state: Parameters<typeof schema.cache.selectById>[0]) =>
    schema.cache.selectById(state, { id: FREEZER_FILTER_ACCOUNTS_CACHE }),
  schema.environments.selectTable,
  (cached, envTable): FreezerActivityAccount[] => {
    const accounts = (cached as FreezerActivityAccount[]) || [];
    const envTableLoaded = Object.keys(envTable).length > 0;
    return accounts.map((acct) => {
      const env = envTable[acct.value];
      return {
        ...acct,
        deleted: env ? !env.activated : envTableLoaded,
      };
    });
  },
);

// Summary formatter
export const formatActivityType = (activityType: string): string => {
  return titleCase(activityType.replace(/\./g, " "));
};

export const formatActivitySummary = (activity: FreezerActivity): string => {
  const parts: string[] = [formatActivityType(activity.activityType)];
  const desc = activity.description;

  if (!desc || Object.keys(desc).length === 0) {
    return parts[0];
  }

  if (desc.automated === true) {
    parts.push("(automated)");
  }
  if (desc.interactive === true) {
    parts.push("(interactive)");
  }
  if (
    desc.authentication_method &&
    typeof desc.authentication_method === "string"
  ) {
    parts.push(`via ${desc.authentication_method}`);
  }
  if (desc.scope && typeof desc.scope === "string") {
    parts.push(`[${desc.scope}]`);
  }
  if (desc.failure_reason && typeof desc.failure_reason === "string") {
    parts.push(`[failure_reason: ${desc.failure_reason}]`);
  }
  if (desc.image_for_deploy && typeof desc.image_for_deploy === "string") {
    parts.push(`→ ${desc.image_for_deploy}`);
  }
  if (typeof desc.duration_seconds === "number") {
    parts.push(`(${desc.duration_seconds}s)`);
  }
  if (Array.isArray(desc.env) && desc.env.length > 0) {
    parts.push(`env: ${desc.env.join(", ")}`);
  }

  return parts.join(" ");
};

const isValidUUID = (value: string): boolean => {
  const uuidRegex =
    /^[a-fA-F0-9]{8}-[a-fA-F0-9]{4}-[1-5a-fA-F0-9]{3}-[89abAB][a-fA-F0-9]{3}-[a-fA-F0-9]{12}$/;
  return uuidRegex.test(value);
};

export const extractOperationIdFromActivityForLogs = (
  activity: FreezerActivity,
): string | null => {
  // TODO - this needs to be improved on downstream. Pending ticket that should allow us to show this.
  // early break if target activity is successful and it was a deprovision operation
  if (
    (activity.activityType.endsWith(".deprovision") ||
      activity.activityType.endsWith(".destroy")) &&
    activity.status === "succeeded"
  )
    return null;

  // early break if virtual operation
  if (
    ["operation.logs", "llmkey.create", "llmkey.revoke"].includes(
      activity.activityType,
    )
  )
    return null;

  if (activity.sourceSystem === "auth-api") return null;

  // if a uuid, it cannot be an operation id
  if (activity.externalId && isValidUUID(activity.externalId)) {
    return null;
  }

  // if isinteger
  if (activity.externalId && /^[+-]?\d+$/.test(activity.externalId)) {
    return activity.externalId;
  }

  return null;
};

export const findTargetResourceOfType = (
  targetResources: FreezerTargetResource[],
  type: string,
): FreezerTargetResource | null => {
  return (
    targetResources.find(
      (tr) => tr.type.toLowerCase() === type.toLowerCase(),
    ) || null
  );
};

export const idForTargetResourceType = (
  targetResources: FreezerTargetResource[],
  type: string,
): string | null => {
  const resource = findTargetResourceOfType(targetResources, type);
  return resource?.id || null;
};

export const getTargetResourceUrlForResource = ({
  resourceId,
  resourceType,
  accountId,
  appId,
  dbId,
}: {
  resourceId: string;
  resourceType: string;
  accountId?: string | null;
  appId?: string | null;
  dbId?: string | null;
}): string | null => {
  switch (resourceType) {
    case "user":
    case "invitation":
    case "invite":
    case "u2fdevice":
      return teamMembersUrl();
    case "operation":
      return operationDetailUrl(resourceId);
    case "app":
      return appDetailUrl(resourceId);
    case "database":
      return databaseDetailUrl(resourceId);
    case "account":
    case "environment":
      return environmentDetailUrl(resourceId);
    case "role":
      return roleDetailUrl(resourceId);
    case "stack":
      return stackDetailEnvsUrl(resourceId);
    case "endpoint":
    case "vhost":
      return endpointDetailActivityUrl(resourceId);
    case "llmkey":
      return llmKeyDetailUrl(resourceId);
    case "certificate":
      return certDetailUrl(resourceId);
    case "source":
      return sourceDetailAppsUrl(resourceId);
    case "orgsetting":
    case "contact":
      return teamContactsUrl();
    case "plan":
      return plansUrl();
    case "externalawsaccount":
      return settingsAccountIntegrationsAwsResourcesUrl(resourceId);
    case "externalawsresource":
      return externalAwsResourceConnectionsUrl(resourceId);
    case "invoice":
      return billingInvoicesUrl();
    case "image":
      return appId ? appDetailUrl(appId) : null;
    case "service":
      return appId ? appServiceUrl(appId, resourceId) : null;
    case "backup":
      return dbId ? databaseBackupsUrl(dbId) : null;
    case "metricdrain":
    case "logdrain":
      return accountId ? environmentIntegrationsUrl(accountId) : null;
    case "databasecredential":
      return dbId ? databaseCredentialsUrl(dbId) : null;
    default:
      return null;
  }
};

export const getTargetResourceUrlForActivity = (
  activity: FreezerActivity,
): string | null => {
  const resourceType = activity.primaryResourceType
    .toLowerCase()
    .replace(/_/g, "");
  const resourceId = activity.primaryResourceId;
  const appId = idForTargetResourceType(activity.targetResources, "app");
  const dbId = idForTargetResourceType(activity.targetResources, "database");
  return getTargetResourceUrlForResource({
    resourceId,
    resourceType,
    accountId: activity.accountId,
    appId,
    dbId,
  });
};

/**
 * Fetches a resource by type and id, with optional support for deleted resources
 */
export function* fetchResourceByType({
  resourceType,
  resourceId,
  withDeleted = false,
}: {
  resourceType: string;
  resourceId: string;
  withDeleted?: boolean;
}): Operation<any> {
  const normalizedType = resourceType.toLowerCase().replace(/_/g, "");

  switch (normalizedType) {
    case "database": {
      return yield* call(() =>
        fetchDatabase.run(fetchDatabase({ id: resourceId, withDeleted })),
      );
    }
    case "app": {
      return yield* call(() =>
        fetchApp.run(fetchApp({ id: resourceId, withDeleted })),
      );
    }
    case "account":
    case "environment": {
      return yield* call(() =>
        fetchEnvironmentById.run(fetchEnvironmentById({ id: resourceId })),
      );
    }
    case "operation": {
      return yield* call(() =>
        fetchOperationById.run(fetchOperationById({ id: resourceId })),
      );
    }
    case "endpoint":
    case "vhost": {
      return yield* call(() =>
        fetchEndpoint.run(fetchEndpoint({ id: resourceId, withDeleted })),
      );
    }
    case "service": {
      return yield* call(() =>
        fetchService.run(fetchService({ id: resourceId, withDeleted })),
      );
    }
    case "metricdrain": {
      return yield* call(() =>
        fetchMetricDrain.run(fetchMetricDrain({ id: resourceId, withDeleted })),
      );
    }
    case "logdrain": {
      return yield* call(() =>
        fetchLogDrain.run(fetchLogDrain({ id: resourceId, withDeleted })),
      );
    }
    default: {
      console.warn(
        `fetchResourceByType: unsupported resource type "${resourceType}"`,
      );
      return null;
    }
  }
}

/**
 * Fetches the primary resource for a given activity
 */
export function* fetchActivityPrimaryResource(
  activity: FreezerActivity,
  withDeleted = false,
): Operation<any> {
  return yield* fetchResourceByType({
    resourceType: activity.primaryResourceType,
    resourceId: activity.primaryResourceId,
    withDeleted,
  });
}

/**
 * Thunk to fetch a resource by type and id for use with useQuery
 */
export const fetchResourceByTypeThunk = thunks.create<{
  resourceType: string;
  resourceId: string;
  withDeleted?: boolean;
}>("fetch-resource-by-type", function* (ctx, next) {
  yield* fetchResourceByType({
    resourceType: ctx.payload.resourceType,
    resourceId: ctx.payload.resourceId,
    withDeleted: ctx.payload.withDeleted || false,
  });
  yield* next();
});

/**
 * Cancel action for resource polling
 */
export const cancelResourceByTypePoll = createAction(
  "cancel-resource-by-type-poll",
);

// Activity Export types

export interface FreezerActivityExportProgress {
  total: number;
  exported: number;
}

export interface FreezerActivityExportResponse {
  id: number;
  organization_id: string;
  account_ids: number[] | null;
  user_id: string;
  export_parameters: Record<string, unknown>;
  filters: Record<string, string>;
  status: string;
  message: string | null;
  max_activity_id: number;
  activity_count_total: number | null;
  activity_count_current: number | null;
  size_bytes: number | null;
  expires_after: string | null;
  started_at: string | null;
  finished_at: string | null;
  created_at: string;
  updated_at: string;
  created_by: string;
  location: string | null;
  progress: FreezerActivityExportProgress | null;
}

export interface FreezerActivityExport {
  id: string;
  organizationId: string;
  accountIds: number[] | null;
  userId: string;
  exportParameters: Record<string, unknown>;
  filters: Record<string, string>;
  status: string;
  message: string;
  maxActivityId: number;
  activityCountTotal: number;
  activityCountCurrent: number;
  sizeBytes: number;
  expiresAfter: string;
  startedAt: string;
  finishedAt: string;
  createdAt: string;
  updatedAt: string;
  createdBy: string;
  location: string;
  progress: FreezerActivityExportProgress;
}

export interface CreateActivityExportProps {
  organizationId: string;
  formatType: "csv" | "jsonl";
  csvHeader?: boolean;
  occurredAfter: string;
  occurredBefore: string;
  types?: string[];
  statuses?: string[];
  actors?: string[];
  envs?: string[];
  q?: string;
  readOnly?: boolean;
}

export const defaultFreezerActivityExport = (
  e: Partial<FreezerActivityExport> = {},
): FreezerActivityExport => {
  const now = new Date().toISOString();
  return {
    id: "",
    organizationId: "",
    accountIds: null,
    userId: "",
    exportParameters: {},
    filters: {},
    status: "queued",
    message: "",
    maxActivityId: 0,
    activityCountTotal: 0,
    activityCountCurrent: 0,
    sizeBytes: 0,
    expiresAfter: "",
    startedAt: "",
    finishedAt: "",
    createdAt: now,
    updatedAt: now,
    createdBy: "",
    location: "",
    progress: { total: 0, exported: 0 },
    ...e,
  };
};

export const deserializeFreezerActivityExport = (
  raw: FreezerActivityExportResponse,
): FreezerActivityExport => {
  return {
    id: `${raw.id}`,
    organizationId: raw.organization_id,
    accountIds: raw.account_ids,
    userId: raw.user_id,
    exportParameters: raw.export_parameters || {},
    filters: raw.filters || {},
    status: raw.status,
    message: raw.message || "",
    maxActivityId: raw.max_activity_id,
    activityCountTotal: raw.activity_count_total || 0,
    activityCountCurrent: raw.activity_count_current || 0,
    sizeBytes: raw.size_bytes || 0,
    expiresAfter: raw.expires_after || "",
    startedAt: raw.started_at || "",
    finishedAt: raw.finished_at || "",
    createdAt: raw.created_at,
    updatedAt: raw.updated_at,
    createdBy: raw.created_by,
    location: raw.location || "",
    progress: raw.progress || { total: 0, exported: 0 },
  };
};

// Activity Export cache keys

export const FREEZER_ACTIVITY_EXPORT_KEY = "freezer-activity-export";
export const FREEZER_ACTIVITY_EXPORT_CURRENT_KEY =
  "freezer-activity-export-current";
export const FREEZER_ACTIVITY_EXPORTS_KEY = "freezer-activity-exports";
export const FREEZER_ACTIVITY_EXPORTS_PAGINATION_KEY =
  "freezer-activity-exports-pagination";

// Activity Export selectors

export const selectFreezerActivityExportById = createSelector(
  (
    state: Parameters<typeof schema.cache.selectById>[0],
    props: { id: string },
  ) =>
    schema.cache.selectById(state, {
      id: `${FREEZER_ACTIVITY_EXPORT_KEY}-${props.id}`,
    }),
  (cached) =>
    (cached as FreezerActivityExport) || defaultFreezerActivityExport(),
);

export const selectFreezerCurrentActivityExport = createSelector(
  (state: Parameters<typeof schema.cache.selectById>[0]) =>
    schema.cache.selectById(state, { id: FREEZER_ACTIVITY_EXPORT_CURRENT_KEY }),
  (cached) =>
    (cached as FreezerActivityExport) || defaultFreezerActivityExport(),
);

export const selectFreezerActivityExportsAsList = createSelector(
  (state: Parameters<typeof schema.cache.selectById>[0]) =>
    schema.cache.selectById(state, { id: FREEZER_ACTIVITY_EXPORTS_KEY }),
  (cached) => (cached as FreezerActivityExport[]) || [],
);

export const selectFreezerActivityExportsPagination = createSelector(
  (state: Parameters<typeof schema.cache.selectById>[0]) =>
    schema.cache.selectById(state, {
      id: FREEZER_ACTIVITY_EXPORTS_PAGINATION_KEY,
    }),
  (cached) => (cached as FreezerPaginationMeta) || defaultFreezerPagination(),
);

// Activity Export endpoints

export const createActivityExport = freezerApi.post<CreateActivityExportProps>(
  "/activity_exports",
  function* (ctx, next) {
    const {
      organizationId,
      formatType,
      csvHeader,
      occurredAfter,
      occurredBefore,
      types,
      statuses,
      actors,
      envs,
      q,
      readOnly,
    } = ctx.payload;

    const body: Record<string, unknown> = {
      organization_id: organizationId,
      format_type: formatType,
      occurred_after: occurredAfter,
      occurred_before: occurredBefore,
    };

    if (csvHeader !== undefined) {
      body.csv_header = csvHeader;
    }
    if (types?.length) {
      body.types = types.join(",");
    }
    if (statuses?.length) {
      body.statuses = statuses.join(",");
    }
    if (actors?.length) {
      body.actors = actors.join(",");
    }
    if (envs?.length) {
      body.envs = envs.join(",");
    }
    if (q) {
      body.q = q;
    }
    if (readOnly) {
      body.read_only = true;
    }

    ctx.request = ctx.req({
      body: JSON.stringify(body),
    });

    yield* next();

    if (!ctx.json.ok) {
      return;
    }

    const raw = ctx.json.value as FreezerActivityExportResponse;
    const activityExport = deserializeFreezerActivityExport(raw);

    yield* schema.update(
      schema.cache.add({
        [`${FREEZER_ACTIVITY_EXPORT_KEY}-${activityExport.id}`]: activityExport,
        [FREEZER_ACTIVITY_EXPORT_CURRENT_KEY]: activityExport,
      }),
    );
  },
);

export const fetchActivityExport = freezerApi.get<{ id: string }>(
  "/activity_exports/:id",
  function* (ctx, next) {
    yield* next();

    if (!ctx.json.ok) {
      return;
    }

    const raw = ctx.json.value as FreezerActivityExportResponse;
    const activityExport = deserializeFreezerActivityExport(raw);

    yield* schema.update(
      schema.cache.add({
        [`${FREEZER_ACTIVITY_EXPORT_KEY}-${activityExport.id}`]: activityExport,
      }),
    );
  },
);

export const fetchActivityExports = freezerApi.get<{
  organizationId: string;
}>("/activity_exports", function* (ctx, next) {
  const { organizationId } = ctx.payload;
  const params = new URLSearchParams();
  params.set("organization_id", organizationId);

  const baseUrl = ctx.req().url;
  ctx.request = ctx.req({ url: `${baseUrl}?${params.toString()}` });

  yield* next();

  if (!ctx.json.ok) {
    return;
  }

  const { activity_exports: rawExports } = ctx.json.value as {
    activity_exports: FreezerActivityExportResponse[];
  };
  const exports = rawExports.map(deserializeFreezerActivityExport);

  const pagination = extractPaginationFromHeaders(ctx.response, 1);

  yield* schema.update(
    schema.cache.add({
      [FREEZER_ACTIVITY_EXPORTS_KEY]: exports,
      [FREEZER_ACTIVITY_EXPORTS_PAGINATION_KEY]: pagination,
    }),
  );
});

export const FREEZER_ACTIVITY_EXPORT_DOWNLOAD_URL_KEY =
  "freezer-activity-export-download-url";

export const selectFreezerActivityExportDownloadUrl = createSelector(
  (state: Parameters<typeof schema.cache.selectById>[0]) =>
    schema.cache.selectById(state, {
      id: FREEZER_ACTIVITY_EXPORT_DOWNLOAD_URL_KEY,
    }),
  (cached) => (cached as string) || "",
);

export const fetchActivityExportFileUrl = freezerApi.get<{ id: string }>(
  "/activity_exports/:id/file",
  function* (ctx, next) {
    yield* next();

    if (!ctx.json.ok) {
      return;
    }

    const { url } = ctx.json.value as { url: string };

    yield* schema.update(
      schema.cache.add({
        [FREEZER_ACTIVITY_EXPORT_DOWNLOAD_URL_KEY]: url,
      }),
    );
  },
);

/**
 * Polling thunk to continuously fetch a resource by type and id
 */
export const pollResourceByTypeThunk = thunks.create<{
  resourceType: string;
  resourceId: string;
  withDeleted?: boolean;
}>(
  "poll-resource-by-type",
  { supervisor: poll(3 * SECONDS, `${cancelResourceByTypePoll}`) },
  function* (ctx, next) {
    yield* fetchResourceByType({
      resourceType: ctx.payload.resourceType,
      resourceId: ctx.payload.resourceId,
      withDeleted: ctx.payload.withDeleted || false,
    });
    yield* next();
  },
);
